import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type components } from "@njbsoft/types";
import { useAppStore } from "@/store";
import { useLab, useDeleteLab } from "@/hooks/use-labs";
import { useRecentPageTitle } from "@/hooks/use-recent-page-title";
import { Breadcrumbs, Button, PageHeader, PageSkeleton } from "@/components/ui";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { LabDetailOverview } from "@/components/labs/lab-detail-overview";
import { LabEditSectionModal, type LabEditSection } from "@/components/labs/lab-edit-section-modal";
import { CertList } from "@/components/labs/cert-list";
import { CertForm } from "@/components/labs/cert-form";
import { ScopeTable } from "@/components/labs/scope-table";
import { Tabs, type TabDef } from "@/components/ui/tabs";

export const Route = createFileRoute("/_authenticated/labs/$labId/")({
  component: LabDetailPage,
});

type TabKey = "overview" | "certifications" | "scopes";

type LabCertification = components["schemas"]["LabCertification"];

function LabDetailPage() {
  const { t } = useTranslation(["labs", "org", "common"]);
  const { labId } = Route.useParams();
  const navigate = useNavigate();
  const orgId = useAppStore((s) => s.activeOrgId);
  const { data: lab, isLoading, isError } = useLab(orgId, labId);
  useRecentPageTitle(lab?.name ?? undefined);
  const deleteLab = useDeleteLab(orgId);

  const [tab, setTab] = useState<TabKey>("overview");
  const [selectedCert, setSelectedCert] = useState<LabCertification | null>(null);
  const [editingCert, setEditingCert] = useState<LabCertification | null>(null);
  const [showAddCert, setShowAddCert] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editSection, setEditSection] = useState<LabEditSection | null>(null);

  if (!orgId) {
    return (
      <div className="p-8">
        <p className="text-muted">{t("noOrgContext", { ns: "org" })}</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-8">
        <PageSkeleton />
      </div>
    );
  }

  if (isError || !lab) {
    return (
      <div className="p-8">
        <div className="rounded-md bg-danger/10 p-4 text-sm text-danger">{t("common:error")}</div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <Breadcrumbs items={[{ label: t("title"), to: "/labs" }, { label: lab.name }]} />
      <PageHeader
        title={lab.name}
        subtitle={lab.legal_name ?? undefined}
        actions={
          <Button
            type="button"
            variant="danger"
            onClick={() => setConfirmDelete(true)}
            disabled={deleteLab.isPending}
          >
            {t("detail.delete")}
          </Button>
        }
      />

      <div className="mt-6">
        <Tabs
          tabs={
            [
              { value: "overview", label: t("detail.tabs.overview") },
              { value: "certifications", label: t("detail.tabs.certifications") },
              { value: "scopes", label: t("detail.tabs.scopes") },
            ] satisfies TabDef<TabKey>[]
          }
          activeTab={tab}
          onTabChange={setTab}
          ariaLabel={t("detail.title", { defaultValue: lab.name })}
        />

        <div className="mt-4" role="tabpanel" id={`tabpanel-${tab}`} aria-labelledby={`tab-${tab}`}>
          {tab === "overview" && (
            <LabDetailOverview
              lab={lab}
              onEditIdentity={() => setEditSection("identity")}
              onEditAddresses={() => setEditSection("addresses")}
              onEditOperational={() => setEditSection("operational")}
            />
          )}
          {tab === "certifications" && (
            <CertList
              orgId={orgId}
              labId={lab.id}
              onAddCertification={() => setShowAddCert(true)}
              onEditCertification={(cert) => setEditingCert(cert)}
              onSelectCertification={(cert) => {
                setSelectedCert(cert);
                setTab("scopes");
              }}
              selectedCertificationId={selectedCert?.id ?? null}
            />
          )}
          {tab === "scopes" && (
            <div className="space-y-4">
              {selectedCert ? (
                <>
                  <div className="rounded-md bg-primary/5 p-3 text-sm text-on-surface">
                    {t("certifications.columns.certificateNumber")}:{" "}
                    {selectedCert.certificate_number}
                  </div>
                  <ScopeTable orgId={orgId} certId={selectedCert.id} />
                </>
              ) : (
                <div className="rounded-md border border-dashed border-gray-300 bg-white p-6 text-center text-sm text-muted dark:border-gray-600 dark:bg-gray-800">
                  {t("certifications.empty.description")}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirmDelete}
        title={t("detail.deleteConfirm.title")}
        message={t("detail.deleteConfirm.body")}
        variant="danger"
        confirmLabel={t("detail.delete")}
        cancelLabel={t("form.cancel")}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={async () => {
          try {
            await deleteLab.mutateAsync(lab.id);
            void navigate({ to: "/labs" });
          } finally {
            setConfirmDelete(false);
          }
        }}
      />

      {showAddCert && (
        <CertForm
          orgId={orgId}
          labId={lab.id}
          open={showAddCert}
          onClose={() => setShowAddCert(false)}
        />
      )}

      {editingCert && (
        <CertForm
          orgId={orgId}
          labId={lab.id}
          cert={editingCert}
          open={editingCert !== null}
          onClose={() => setEditingCert(null)}
        />
      )}

      {editSection && (
        <LabEditSectionModal
          open={editSection !== null}
          onClose={() => setEditSection(null)}
          orgId={orgId}
          lab={lab}
          section={editSection}
        />
      )}
    </div>
  );
}
